import { BrandResponse } from "@/js/repository/BrandRepository";

// 카테고리 그룹 타입 정의 (언어 무관하게 first_level_category_id로 분류)
export interface CategoryGroup {
  id: string;
  name: string;
  /** first_level_category_id 목록 (코드 기준 분류, 언어 대응) */
  categoryIds: number[];
  brands?: BrandResponse[];
  priority: number; // 우선순위 (낮을수록 높은 우선순위)
}

// 카테고리 대분류 정의 - 코드(first_level_category_id) 기준, 주석은 참고용 텍스트(로케일별 상이)
// ID 목록은 GET /api/categories/list?locale=ko 등으로 조회 가능
export const CATEGORY_GROUPS: CategoryGroup[] = [
  {
    id: 'electronics',
    name: 'category.electronics',
    categoryIds: [
      7,        // Computer & Office / 컴퓨터 및 오피스
      44,       // Consumer Electronics / 소비자 가전
      502,      // Electronic Components & Supplies / 전자 부품 및 소모품
      509,      // Phones & Telecommunications / 전화 및 통신
      202192403,// Phones & Telecommunications Accessories / 전화 및 통신 액세서리
      30,       // Security & Protection / 안전 및 보안
    ],
    priority: 1,
  },
  {
    id: 'fashion',
    name: 'category.fashion',
    categoryIds: [
      200000345, // Women's Clothing / 여성 의류
      200000343, // Men's Clothing / 남성 의류
      200000297, // Apparel Accessories / 패션잡화
      36,       // Jewelry & Accessories / 주얼리 및 액세서리
      1511,     // Watches / 시계
      200574005, // Underwear / 속옷 및 홈웨어
      200165144, // Hair Extensions & Wigs / 머리 연장 & 가발
      200000532, // Novelty & Special Use / 테마의류
      320,      // Weddings & Events / 웨딩 및 이벤트
      1524,     // Luggage & Bags / 가방 및 캐리어
      322,      // Shoes / 신발
    ],
    priority: 2,
  },
  {
    id: 'health',
    name: 'category.health',
    categoryIds: [
      66, // Beauty & Health / 뷰티 & 헬스
    ],
    priority: 3,
  },
  {
    id: 'home',
    name: 'category.home',
    categoryIds: [
      6,    // Home Appliances / 가전 제품
      15,   // Home & Garden / 홈 & 가든
      13,   // Home Improvement / 생활용품
      1503, // Furniture / 가구
      39,   // Lights & Lighting / 조명
    ],
    priority: 4,
  },
  {
    id: 'sports',
    name: 'category.sports',
    categoryIds: [
      18,        // Sports & Entertainment / 스포츠 & 엔터테인먼트
      201768104, // Sports Shoes, Clothing & Accessories / 스포츠 신발, 의류 및 액세서리
      34,        // Automobiles, Parts & Accessories / 자동차, 부품 & 악세사리
      201355758, // Motorcycle Equipments & Parts / 오토바이 장비 및 부품
    ],
    priority: 5,
  },
  {
    id: 'kids',
    name: 'category.kids',
    categoryIds: [
      1501, // Mother & Kids / 어머니 & 아이
      26,   // Toys & Hobbies / 완구 및 취미
    ],
    priority: 6,
  },
  {
    id: 'office',
    name: 'category.office',
    categoryIds: [
      21,        // Office & School Supplies / 사무실 및 학교 용품
      202216001, // Industrial & Business / 산업 및 비즈니스
      1420,      // Tools / 도구
    ],
    priority: 7,
  },
  {
    id: 'other',
    name: 'category.other',
    categoryIds: [], // 위 카테고리에 속하지 않는 모든 브랜드
    priority: 999,
  },
];

// 카테고리 그룹별로 브랜드 분류 (categoryIds 기준)
export function groupBrandsByCategory(brands: BrandResponse[]): CategoryGroup[] {
  const grouped: CategoryGroup[] = CATEGORY_GROUPS.map(group => ({
    ...group,
    brands: [],
  }));

  brands.forEach(brand => {
    if (!brand.categoryIds || brand.categoryIds.length === 0) {
      const otherGroup = grouped.find(g => g.id === 'other');
      if (otherGroup && otherGroup.brands) {
        otherGroup.brands.push(brand);
      }
      return;
    }

    let highestPriorityGroup: CategoryGroup | null = null;
    let highestPriority = Infinity;

    for (const categoryId of brand.categoryIds) {
      for (const group of grouped) {
        if (group.categoryIds.includes(categoryId)) {
          if (group.priority < highestPriority) {
            highestPriority = group.priority;
            highestPriorityGroup = group;
          }
        }
      }
    }

    if (highestPriorityGroup && highestPriorityGroup.brands) {
      highestPriorityGroup.brands.push(brand);
    } else {
      const otherGroup = grouped.find(g => g.id === 'other');
      if (otherGroup && otherGroup.brands) {
        otherGroup.brands.push(brand);
      }
    }
  });

  return grouped
    .filter(group => group.brands && group.brands.length > 0)
    .map(group => ({
      ...group,
      brands: group.brands ? [...group.brands].sort((a, b) => b.count - a.count) : [],
    }))
    .sort((a, b) => (b.brands?.length || 0) - (a.brands?.length || 0));
}

// 현재 브랜드의 카테고리 그룹 ID 찾기 (categoryIds 기준)
export function findBrandCategoryGroupId(brand: BrandResponse): string | null {
  if (!brand.categoryIds || brand.categoryIds.length === 0) {
    return 'other';
  }

  let highestPriorityGroupId = 'other';
  let highestPriority = 999;

  for (const categoryId of brand.categoryIds) {
    for (const group of CATEGORY_GROUPS) {
      if (group.categoryIds.includes(categoryId)) {
        if (group.priority < highestPriority) {
          highestPriority = group.priority;
          highestPriorityGroupId = group.id;
        }
      }
    }
  }

  return highestPriorityGroupId;
}
